/**
 * UnifiedFilterSheet Component
 * 
 * Global filter sheet with consistent location filtering across all pages.
 * Used by: Books, Shop, Jobs/Internships, and any listing-based page.
 * 
 * Features:
 * - Shared country/city location system
 * - Consistent Apply/Clear All buttons
 * - Strict filtering (no fallback results)
 * - Page-specific filters via children
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { X, Check, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
} from './ui/sheet';
import LocationSelector from './LocationSelector';
import useLocationFilter from '../hooks/useLocationFilter';
import './UnifiedFilterSheet.css';

/**
 * Default filter state (can be extended per page)
 */
export const DEFAULT_FILTERS = {
  country: '',
  city: '',
  sort: 'newest',
};

/**
 * Count active filters
 */
export function countActiveFilters(filters, defaultFilters = DEFAULT_FILTERS) {
  if (!filters) return 0;
  let count = 0;
  
  Object.keys(filters).forEach(key => {
    const value = filters[key];
    const defaultValue = defaultFilters[key];
    
    // Skip if value is default or empty
    if (value === defaultValue) return;
    if (value === '' || value === null || value === undefined) return;
    if (key === 'sort' && value === 'newest') return;
    
    count++;
  });
  
  return count;
}

/**
 * UnifiedFilterSheet Component
 */
export default function UnifiedFilterSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  onCoordsResolved,
  title,
  children,
  showLocation = true,
  defaultFilters = DEFAULT_FILTERS,
}) {
  // Get language/RTL from document
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
  
  // Draft state (only applied on "Apply" click)
  const [draft, setDraft] = useState({ ...defaultFilters });
  
  // Location filter hook
  const {
    countries,
    citiesForCountry,
    loading: loadingLocations,
  } = useLocationFilter();

  // Get cities for draft country
  const draftCities = useMemo(() => {
    if (!draft.country) return [];
    const country = countries.find(c => c.key === draft.country);
    return country?.cities || [];
  }, [draft.country, countries]);

  // Sync draft from applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({ ...filters });
    }
  }, [open, filters]);

  // Count active draft filters
  const draftActiveCount = useMemo(() => countActiveFilters(draft, defaultFilters), [draft, defaultFilters]);

  // Handle country change (clears city)
  const handleCountryChange = useCallback((country) => {
    setDraft(prev => ({ ...prev, country, city: '' }));
  }, []);

  // Handle city change
  const handleCityChange = useCallback((city) => {
    setDraft(prev => ({ ...prev, city }));
  }, []);

  // Handle any filter change (for children)
  const handleFilterChange = useCallback((key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear all filters — fires parent's shared reset helper when provided,
  // otherwise falls back to just resetting local draft. Ensures the sheet's
  // "Clear All" uses the same single-source-of-truth reset as the chip bar.
  const handleClearAll = useCallback(() => {
    setDraft({ ...defaultFilters });
    if (typeof onClear === 'function') {
      onClear();
    } else {
      // No parent handler — still commit the cleared draft upstream
      onFiltersChange?.({ ...defaultFilters });
    }
  }, [defaultFilters, onClear, onFiltersChange]);

  // Apply filters
  const handleApply = useCallback(() => {
    onFiltersChange({ ...draft });
    onApply?.();
    onOpenChange(false);
  }, [draft, onFiltersChange, onApply, onOpenChange]);

  // Close without applying
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Labels
  const labels = {
    title: title || (isRTL ? 'الفلاتر' : 'Filters'),
    clearAll: isRTL ? 'مسح الكل' : 'Clear All',
    applyFilters: isRTL ? 'تطبيق الفلاتر' : 'Apply Filters',
    // When nothing has changed from defaults, the button is still always
    // tappable — but the label honestly says "Show Results" (or "عرض النتائج"
    // in AR) so the user understands tapping will close the sheet and show
    // the default-sorted (Newest) feed. Prevents the "looks disabled" feeling.
    showResults: isRTL ? 'عرض النتائج' : 'Show Results',
    location: isRTL ? 'الموقع' : 'Location',
    country: isRTL ? 'الدولة' : 'Country',
    city: isRTL ? 'المدينة' : 'City',
    searchCountry: isRTL ? 'ابحث عن دولة...' : 'Search country...',
    searchCity: isRTL ? 'ابحث عن مدينة...' : 'Search city...',
  };
  const applyLabel = draftActiveCount > 0 ? labels.applyFilters : labels.showResults;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="unified-filter-sheet"
        hideCloseButton={true}
        ariaLabel={labels.title}
      >
        {/* Header */}
        <div className="ufs-header" data-testid="ufs-header">
          <button 
            className="ufs-close-btn" 
            onClick={handleClose} 
            aria-label="Close"
            data-testid="ufs-close-btn"
          >
            <X size={20} />
          </button>
          <div className="ufs-header-center">
            <h2 className="ufs-title">{labels.title}</h2>
            {draftActiveCount > 0 && (
              <span className="ufs-badge" data-testid="ufs-filter-count">
                {draftActiveCount}
              </span>
            )}
          </div>
          <div style={{ width: 36 }} />
        </div>

        {/* Body */}
        <div className="ufs-body">
          {/* Page-specific filters (passed as children) */}
          {typeof children === 'function' ? children({ draft, setDraft: handleFilterChange }) : children}

          {/* Location Section - Shared across all pages */}
          {showLocation && (
            <div className="ufs-section ufs-location-section">
              <LocationSelector
                countries={countries}
                citiesForCountry={draftCities}
                selectedCountry={draft.country}
                selectedCity={draft.city}
                onCountryChange={handleCountryChange}
                onCityChange={handleCityChange}
                onCoordsResolved={onCoordsResolved}
                isRTL={isRTL}
                labels={{
                  location: labels.location,
                  country: labels.country,
                  city: labels.city,
                  searchCountry: labels.searchCountry,
                  searchCity: labels.searchCity,
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ufs-footer" data-testid="ufs-footer">
          <button
            className={`ufs-clear-btn ${draftActiveCount === 0 ? 'disabled' : ''}`}
            onClick={handleClearAll}
            disabled={draftActiveCount === 0}
            data-testid="ufs-clear-all-btn"
          >
            <Trash2 size={16} />
            {labels.clearAll}
          </button>

          <button 
            className="ufs-apply-btn" 
            onClick={handleApply}
            data-testid="ufs-apply-btn"
            aria-label={applyLabel}
          >
            <Check size={18} />
            {applyLabel}
            {draftActiveCount > 0 && (
              <span className="ufs-apply-count">{draftActiveCount}</span>
            )}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
